import React, { createContext, useContext, useState, useCallback, useRef } from "react";

/**
 * Puente entre el HeaderComercial (donde vive el input de búsqueda de direcciones)
 * y el mapa de prospección (que ejecuta la búsqueda: centra, deja el pin, o abre
 * el alta manual). El header solo muestra el buscador cuando `activo` es true, lo
 * que el mapa activa al montarse y desactiva al desmontarse → el buscador aparece
 * únicamente en la sección Mapa, sin que el header sepa qué pantalla está abierta.
 */
const MapaBuscadorContext = createContext(null);

export function MapaBuscadorProvider({ children }) {
  const [activo, setActivo] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const handlerRef = useRef(null);

  // El mapa registra su handler al montar (fn) y lo limpia al desmontar (null).
  const registrarBuscador = useCallback((fn) => {
    handlerRef.current = fn;
    setActivo(!!fn);
    if (!fn) setBuscando(false);
  }, []);

  // El header dispara la búsqueda. Acepta texto libre (el mapa geocodifica) o un
  // lugar ya resuelto por el autocompletado {lat,lng,direccion} (sin re-geocodear).
  const buscar = useCallback(async (entrada) => {
    if (!handlerRef.current || !entrada) return;
    const arg = typeof entrada === "string" ? entrada.trim() : entrada;
    if (!arg || (typeof arg === "string" && !arg)) return;
    setBuscando(true);
    try { await handlerRef.current(arg); }
    finally { setBuscando(false); }
  }, []);

  return (
    <MapaBuscadorContext.Provider value={{ activo, buscando, registrarBuscador, buscar }}>
      {children}
    </MapaBuscadorContext.Provider>
  );
}

export function useMapaBuscador() {
  return useContext(MapaBuscadorContext) || {
    activo: false, buscando: false, registrarBuscador: () => {}, buscar: () => {},
  };
}
